import { z } from 'zod';

/**
 * One museum's configuration checklist for one component — the unwrapped body
 * of `GET /api/tenants/:tenantId/components/:componentId/settings`.
 *
 * Unwrapped on purpose, and worth stating because every other read in this
 * feature is not: `GET /api/components` answers `{ components: [...] }` because
 * a list wants room for a cursor beside it, while this route answers one view
 * and has nothing to sit next to. See `ComponentSettingsView` in the API's
 * `get-component-settings.use-case.ts`, which this mirrors field for field.
 *
 * Parsed rather than cast, for the reason `feature-flags/schemas/flag.ts`
 * gives: this is a network boundary, and a drifted response should fail here
 * rather than render as `undefined` inside a card.
 *
 * ## `activated` is not stored anywhere, and neither side pretends it is
 *
 * The API reads it back off the grantors on the museum's override rows, which
 * is where activation writes it. Nothing in the tracking table answers it — the
 * table holds progress and never entitlement — so a screen that inferred
 * "activated" from a non-empty `settings` list would be inventing a second
 * answer that disagrees with the first. A deactivated museum keeps its rows.
 */

/**
 * How a setting gets done: by hand in some other console, or by the owning
 * service reporting back. Enumerated, unlike {@link SettingStatus} below,
 * because the API's own `ComponentSettingKind` is a closed union of exactly
 * these two and a third would need a UI affordance that does not exist yet.
 */
export const ComponentSettingKind = z.enum(['manual', 'automatic']);
export type ComponentSettingKind = z.infer<typeof ComponentSettingKind>;

/**
 * How far along one setting is — **a plain string, deliberately**.
 *
 * The Backoffice owns the row but the backend updates it, and those are two
 * deployments on two release trains: the set of values the backend writes grows
 * before this app is redeployed to know about them. Enumerating them here would
 * turn the day a listener starts writing `awaiting_review` into this tab going
 * dark for every museum holding that row — a Backoffice outage caused by a
 * backend deploy that did nothing wrong.
 *
 * The API already answers the only question a closed set would have been for:
 * `known` says whether this value is one the UI has copy for. So the string is
 * carried verbatim and the card renders an unknown one as the unknown thing it
 * is, rather than coercing it to `not_started` — which would tell an operator
 * that work nobody has looked at is work nobody has started.
 */
const SettingStatus = z.string();

export const ComponentSettingStatusView = z.object({
    id: z.string(),
    label: z.string(),
    kind: ComponentSettingKind,
    status: SettingStatus,
    /**
     * Whether this build knows how to render {@link SettingStatus}. Computed by
     * the API rather than here so that "the backend shipped a status we do not
     * know" is answered once, by the app that owns the catalog, instead of by
     * each screen keeping its own copy of the list and drifting from it.
     */
    known: z.boolean(),
    /**
     * `null` when no row exists for this setting yet. Not optional: the API
     * always sends the field and always sends `null` for absence, and accepting
     * `undefined` too would hide a response that stopped sending it.
     */
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
export type ComponentSettingStatusView = z.infer<typeof ComponentSettingStatusView>;

export const ComponentSettingsView = z.object({
    componentId: z.string(),
    tenantId: z.string(),
    activated: z.boolean(),
    /**
     * Empty for a museum that was never sold this component. **Non-empty for
     * one that was sold it and then deactivated** — deactivation removes the
     * grants and keeps the rows, so a museum re-enabled a month later does not
     * lose the work it finished. Those two are the only thing that tells the
     * "never activated" card from the "off, already provisioned" one.
     */
    settings: z.array(ComponentSettingStatusView),
});
export type ComponentSettingsView = z.infer<typeof ComponentSettingsView>;

/**
 * What one museum holds — the body of `GET /api/tenants/:tenantId/components`.
 *
 * Wrapped, unlike {@link ComponentSettingsView} above and for the reason the
 * route states: a list wants room beside it. In particular this answer is not
 * yet complete, and the day it can say so that field needs somewhere to go
 * other than inside the list of components.
 *
 * ## An empty `components` is not "holds nothing"
 *
 * It means no subscription row carries this museum's index key. Holdings
 * predating `D43` are recorded only as grants on the member flags, and rows
 * written before `GSI2` existed are absent until they are rewritten. So this is
 * the reading the card **unions** with the grants on the museum's flags, never
 * the one that replaces them — see `component-card.ts`. For a component with no
 * member flags the grant reading is the empty one instead, which is the whole
 * reason this route exists.
 */
export const TenantComponentHolding = z.object({
    componentId: z.string(),
    activatedAt: z.string(),
    /** Empty is the system actor's shape, not a missing field. */
    activatedBy: z.string(),
});
export type TenantComponentHolding = z.infer<typeof TenantComponentHolding>;

export const TenantComponentsView = z.object({
    components: z.array(TenantComponentHolding),
});
export type TenantComponentsView = z.infer<typeof TenantComponentsView>;
